/**
 * CelebrationProvider — app-root mount for the celebration system
 * (Plan 02-15 / D-10 / CEL-01..03 / UI-SPEC §11).
 *
 * Mounts at the app root inside QueryClientProvider. Subscribes to the
 * /ws hub via useCelebration and routes WS-driven events into the four
 * tier renderers (L1/L2/L3/L4). Only one L4 takeover is visible at a
 * time — subsequent achievement unlocks queue and fire when the active
 * one closes.
 *
 * #155 (redesign steg 5b): L3 er en hel skjerm med «Fortsett» (D7), så også
 * L3 vises én om gangen, i rekkefølge. Kommer et merke samtidig (den 5.
 * godkjente oppgaven), ligger L4 over L3: merket lukker seg selv etter 5 s,
 * og poengskjermen står igjen under. Det L3-skjermen trenger og rammen ikke
 * har, slås opp her på klienten:
 *   - oppgavetittelen fra spørringsbufferen (tavla, lekser, historikken) i
 *     det øyeblikket hendelsen kommer, før ['chores'] hentes på nytt — og ellers
 *     fra /api/chores/completed?days=1, som også har godkjennerens navn;
 *   - godkjennerens navn fra /api/members/basic (`approved_by` i rammen);
 *   - streaken fra /api/streaks/:id, som invalideres når hendelsen kommer,
 *     fordi serveren oppdaterer den i samme godkjenning.
 *
 * Leverandøren står utenfor RouterProvider, så «Se alle merkene mine» på L4
 * navigerer gjennom `navigate`-propen App gir (router.navigate).
 *
 * Viewer identity is resolved from /api/auth/me on mount. Until the
 * round-trip lands, the provider renders only its children — celebration
 * subscription is gated on a known viewerId so we don't fire for the
 * wrong member.
 *
 * D-10: triggers come from WS events (NOT request resolution). The
 * useCelebration hook enforces this; the provider only routes the
 * events it receives.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useDeviceClass } from '../dashboard/DashboardStore';
import { useCompletedChores } from '../chores/useChoresQuery';
import { useFamilyMembers } from '../chores/useFamilyMembers';
import { useStreak } from '../streaks/useStreakQueries';
import { useCelebration, type CelebrationEvent } from './useCelebration';
import { L1LockIn } from './L1LockIn';
import { L2DoneGlow } from './L2DoneGlow';
import { L3ApprovedBurst } from './L3ApprovedBurst';
import { L4Takeover } from './L4Takeover';

interface SessionMember {
  id: string;
  short_name?: string;
}

function useViewer(): SessionMember | null {
  const [me, setMe] = useState<SessionMember | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await globalThis.fetch('/api/auth/me', {
          credentials: 'same-origin',
        });
        if (!r.ok) return;
        const body = (await r.json()) as { member?: SessionMember };
        if (cancelled || !body.member) return;
        setMe(body.member);
      } catch {
        /* offline / unauthed — leave null; provider runs in pass-through */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return me;
}

/** Spørringene som kan holde oppgaven som nettopp ble godkjent. */
const TITLE_SOURCES = [['chores'], ['homework'], ['chores-completed']] as const;

/**
 * Tittelen på en oppgave fra bufferen, eller undefined. Leses idet hendelsen
 * kommer: godkjenningen invaliderer ['chores'], og den nye tavla har ikke
 * lenger oppgaven.
 */
function titleFromCache(qc: QueryClient, instanceId: string): string | undefined {
  for (const queryKey of TITLE_SOURCES) {
    for (const [, data] of qc.getQueriesData<unknown>({ queryKey: [...queryKey] })) {
      if (!Array.isArray(data)) continue;
      const row = data.find(
        (r: unknown): r is { id: string; title_snapshot: string } =>
          typeof r === 'object' &&
          r !== null &&
          (r as { id?: unknown }).id === instanceId &&
          typeof (r as { title_snapshot?: unknown }).title_snapshot === 'string',
      );
      if (row) return row.title_snapshot;
    }
  }
  return undefined;
}

interface CelebrationProviderProps {
  children?: ReactNode;
  /** Test-only override — bypasses the auth round-trip for deterministic tests. */
  viewerOverride?: SessionMember | null;
  /** Navigering for knappene på feiringene; leverandøren står utenfor ruteren. */
  navigate?: (to: string) => void;
}

export function CelebrationProvider({
  children,
  viewerOverride,
  navigate,
}: CelebrationProviderProps): ReactNode {
  const fetchedViewer = useViewer();
  const viewer = viewerOverride !== undefined ? viewerOverride : fetchedViewer;
  const qc = useQueryClient();

  const deviceClass = useDeviceClass();
  const viewport: 'tablet' | 'phone' =
    deviceClass === 'tablet' ? 'tablet' : 'phone';

  // Active celebration queue. Every queued L1/L2 event renders its own child
  // concurrently — they are non-blocking and each removes itself when its
  // lifetime expires. L3 and L4 each form a strict queue, one-at-a-time
  // globally (UI-SPEC §10; L3 since #155, when it became a full screen). Before
  // #155 two approvals inside the 3s toast window were two live L3s, and that
  // overlap is exactly what issue #82's keying bug showed up in.
  const [active, setActive] = useState<CelebrationEvent[]>([]);

  useCelebration({
    viewerId: viewer?.id ?? null,
    viewport,
    onEvent: (ev) => {
      if (ev.tier === 'l3' && ev.instanceId) {
        const title = titleFromCache(qc, ev.instanceId);
        if (title) ev = { ...ev, title };
        // Serveren har oppdatert streaken i samme godkjenning.
        if (viewer?.id) void qc.invalidateQueries({ queryKey: ['streaks', viewer.id] });
      }
      setActive((prev) => [...prev, ev]);
    },
  });

  // The active L4 / L3 is the first of its tier in the queue (FIFO), one at a
  // time each. L1/L2 render independently and remove themselves on completion.
  const activeL4 = active.find((e) => e.tier === 'l4') ?? null;
  const activeL3 = active.find((e) => e.tier === 'l3') ?? null;
  const activeL1s = active.filter((e) => e.tier === 'l1');
  const activeL2s = active.filter((e) => e.tier === 'l2');

  // Oppslagene for L3-skjermen (se headeren). Bare mens en L3 vises.
  const members = useFamilyMembers(activeL3 !== null);
  const needsHistory = activeL3 !== null && (!activeL3.title || !activeL3.approvedBy);
  const recent = useCompletedChores(1, needsHistory);
  const streak = useStreak(viewer?.id ?? '', activeL3 !== null);
  const recentRow = activeL3
    ? (recent.data ?? []).find((r) => r.id === activeL3.instanceId)
    : undefined;
  const approver = activeL3?.approvedBy
    ? (members.data ?? []).find((m) => m.id === activeL3.approvedBy)
    : undefined;
  const approverName =
    (approver ? approver.short_name || approver.name : undefined) ??
    recentRow?.approver_short_name ??
    undefined;
  const l3Title = activeL3?.title ?? recentRow?.title_snapshot ?? undefined;

  const removeEvent = (id: string) => {
    setActive((prev) => prev.filter((e) => e.id !== id));
  };

  // Every key below is the event's own id, and issue #82 is the reason (see
  // useCelebration.ts's `nextEventId`). A React key that is not stable per
  // event is not a cosmetic choice here: the tier components fire once per
  // MOUNT, guarded by refs (issue #81), so the key IS the celebration's
  // identity. The two ways to get it wrong have opposite symptoms and both
  // were live before #82:
  //
  //   - Too unstable (L1/L2/L3 keyed by index in the FILTERED list): removing
  //     the finished head of a two-item queue shifted the survivor's key,
  //     React remounted it, every ref guard reset, and the celebration
  //     replayed — two approvals, three toasts.
  //   - Too stable (L4 keyed by `achievementKey`): two queued unlocks of the
  //     same achievement shared a key, so it did NOT change when the queue
  //     advanced. React reused the instance with `open` already false and an
  //     auto-dismiss effect that never re-arms, and the second unlock was
  //     never shown, blocking every L4 behind it. (Note the hub does not
  //     replay — `apps/api/src/ws/hub.ts` is fire-and-forget — so this needs
  //     a duplicated unlock emission rather than a redelivered one. Either
  //     way the key must be per-event: the queue has to survive duplicates
  //     whatever put them there.)
  return (
    <>
      {children}

      {/* L1 — chore claim lock-in. Renders one entry per queued event. */}
      {activeL1s.map((ev) => (
        <L1LockIn
          key={`l1-${ev.id}`}
          instanceId={ev.instanceId ?? ''}
          reducedMotion={ev.reducedMotion}
          onComplete={() => removeEvent(ev.id)}
        />
      ))}

      {/* L2 — chore complete glow. Renders one entry per queued event. */}
      {activeL2s.map((ev) => (
        <L2DoneGlow
          key={`l2-${ev.id}`}
          instanceId={ev.instanceId ?? ''}
          reducedMotion={ev.reducedMotion}
          onComplete={() => removeEvent(ev.id)}
        />
      ))}

      {/* L3 — godkjent-skjermen (#155). Strict one-at-a-time, FIFO. */}
      {activeL3 ? (
        <L3ApprovedBurst
          key={`l3-${activeL3.id}`}
          instanceId={activeL3.instanceId ?? ''}
          points={activeL3.points ?? 0}
          nick={viewer?.short_name ?? ''}
          prevBalance={activeL3.prevBalance}
          approverName={approverName}
          title={l3Title}
          streakDays={streak.data?.current_streak}
          viewport={activeL3.viewport}
          reducedMotion={activeL3.reducedMotion}
          onComplete={() => removeEvent(activeL3.id)}
        />
      ) : null}

      {/* L4 — fullscreen takeover. Strict one-at-a-time. Etter L3 i treet, så
          et merke som kommer samtidig ligger øverst. */}
      {activeL4 ? (
        <L4Takeover
          key={`l4-${activeL4.id}`}
          achievementKey={activeL4.achievementKey ?? ''}
          viewport={activeL4.viewport}
          reducedMotion={activeL4.reducedMotion}
          onComplete={() => removeEvent(activeL4.id)}
          {...(navigate ? { onSeeAll: () => navigate('/utmerkelser') } : {})}
        />
      ) : null}
    </>
  );
}
